<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import SupportMeasureIcon from '@/assets/icons/SupportMeasureIcon.vue';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useSupportMeasureEntry } from '@/composables/supportMeasureEntry/useSupportMeasureEntry';
import { useSupportMeasureCheckout } from '@/composables/supportMeasureEntry/useSupportMeasureCheckout';
import { useSupportMeasureFeature } from '@/composables/supportMeasureEntry/useSupportMeasureFeature';
import { useWeekNumberSettings } from '@/composables/useWeekNumberSettings';
import TimelineItemList from '@/core/timeline/TimelineItemList.vue';
import TimelineRail from '@/core/timeline/TimelineRail.vue';
import TimelineWeekHeading from '@/core/timeline/TimelineWeekHeading.vue';
import { getTimelineWeekLabel } from '@/utils/date.timeline.utils';
import { computed, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import SupportMeasureActivityCompactItem from './SupportMeasureActivityCompactItem.vue';
import SupportMeasureCompletionItem from './SupportMeasureCompletionItem.vue';
import SupportMeasureProtocolEntryItem from './SupportMeasureProtocolEntryItem.vue';
import SupportMeasureQuickEntryComposer from './SupportMeasureQuickEntryComposer.vue';
import {
  buildSupportMeasureTimelineItems,
  groupSupportMeasureTimelineItems,
  type TSupportMeasureTimelineItem,
} from './supportMeasureProtocolTimeline.utils';

const { t } = useI18n();
const { confirm } = useConfirmDialog();
const { updateProtocolEntry, deleteProtocolEntry, setProtocolEntryRating } = useSupportMeasureEntry();

const props = defineProps<{
  entryId: number;
  schoolSubjectId: number;
  schoolClassId: number | null;
  protocol: Components.Schemas.SupportMeasureEntryDto['protocol'];
  activities: Components.Schemas.SupportMeasureEntryDto['activities'];
  students: Components.Schemas.SupportMeasureEntryDto['students'];
  selectedStudentId: number | null;
  subjectColor: string;
  readonly?: boolean;
  completedAt: string | null;
  completionNote: string | null;
}>();
const {
  entryId,
  schoolSubjectId,
  schoolClassId,
  protocol,
  activities,
  students,
  selectedStudentId,
  subjectColor,
  readonly,
  completedAt,
  completionNote,
} = toRefs(props);
const { hasSupportMeasures } = useSupportMeasureFeature();
const { showProductCheckoutDialog } = useSupportMeasureCheckout();
const { weekNumberSettings } = useWeekNumberSettings();

const editingId = ref<number | null>(null);
const timelineItems = computed<TSupportMeasureTimelineItem[]>(() =>
  buildSupportMeasureTimelineItems({
    activities: activities.value,
    completedAt: completedAt.value,
    protocol: protocol.value,
    selectedStudentId: selectedStudentId.value,
  }),
);
const timelineGroups = computed(() => groupSupportMeasureTimelineItems(timelineItems.value));

const keyForTimelineItem = (item: TSupportMeasureTimelineItem): string => item.key;
const markerColorFor = (item: TSupportMeasureTimelineItem): string | undefined =>
  item.kind === 'protocol' ? subjectColor.value : undefined;
const markerClassFor = (item: TSupportMeasureTimelineItem): string | undefined => {
  if (item.kind === 'completion') return 'bg-surface-green';
  if (item.kind === 'activity') return 'border-2 border-gray bg-white';
  return undefined;
};
const contentSpacingClassFor = (
  item: TSupportMeasureTimelineItem,
  nextItem: TSupportMeasureTimelineItem | undefined,
  isLastItem: boolean,
): string => {
  if (isLastItem) return '';
  return item.kind === 'activity' && nextItem?.kind === 'activity' ? 'pb-sm' : 'pb-xl';
};

const handleRequestEdit = (protocolEntryId: number): void => {
  if (!hasSupportMeasures.value) {
    showProductCheckoutDialog();
    return;
  }
  editingId.value = protocolEntryId;
};

const removeEntry = async (protocolEntryId: number): Promise<void> => {
  const confirmed = await confirm({
    title: t('supportMeasureEntry.deleteProtocolTitle'),
    description: t('supportMeasureEntry.deleteProtocolDescription'),
    confirmButtonLabel: t('general.delete'),
    declineButtonLabel: t('general.cancel'),
    theme: 'danger',
  });
  if (!confirmed) return;
  await deleteProtocolEntry(protocolEntryId);
};

const handleRate = async (
  protocolEntryId: number,
  rating: Components.Schemas.SupportMeasureProtocolEntryRating | null,
): Promise<void> => {
  await setProtocolEntryRating(protocolEntryId, rating);
};

// inline rating change from the read view — replace-set the entry's observations with the new level.
// A null level is ignored (clearing/removing a competence is only possible via the edit composer).
const handleObservationLevelChange = async (
  protocolEntryId: number,
  payload: { competenceId: number; level: number | null },
): Promise<void> => {
  if (payload.level == null) return;
  const newLevel = payload.level;
  const protocolEntry = protocol.value.find((entry) => entry.id === protocolEntryId);
  if (!protocolEntry) return;
  const competenceObservations: Components.Schemas.ProtocolCompetenceObservationInputDto[] =
    protocolEntry.competenceObservations
      .map((observation) => ({
        studentId: observation.studentId,
        competenceId: observation.competenceId,
        competenceAssessmentId: observation.competenceAssessmentId,
        level: observation.competenceId === payload.competenceId ? newLevel : observation.performanceLevelV2Id,
      }))
      .filter((row): row is typeof row & { level: number } => row.level != null)
      .map(({ studentId, competenceId, competenceAssessmentId, level }) => ({
        studentId,
        competenceId,
        competenceAssessmentId,
        performanceLevelV2Id: level,
      }));
  await updateProtocolEntry(protocolEntryId, { competenceObservations });
};
</script>

<template>
  <div class="flex flex-col gap-lg pb-lg">
    <div
      v-if="!timelineItems.length"
      class="text-secondary mx-auto flex max-w-80 flex-1 flex-col items-center justify-center gap-xs py-2xl text-center leading-body"
    >
      <SupportMeasureIcon class="mb-md h-button w-button shrink-0 text-secondary" />
      <p class="text-secondary font-medium">
        {{ readonly ? $t('general.noEntries') : $t('general.noEntriesYet') }}
      </p>
      <p v-if="!readonly" class="text-body leading-body">{{ $t('supportMeasureEntry.protocolEmptyHint') }}</p>
    </div>

    <div v-else class="flex flex-col">
      <template v-for="([groupKey, groupItems], groupIndex) in timelineGroups" :key="groupKey">
        <TimelineWeekHeading
          :class="{ 'mt-lg': groupIndex > 0 }"
          :label="getTimelineWeekLabel(groupItems[0].date, weekNumberSettings)"
        />

        <TimelineItemList :get-key="keyForTimelineItem" :items="groupItems">
          <template #default="{ index, item, nextItem, rail }">
            <div class="flex gap-lg" :class="groupIndex === 0 && index === 0 ? 'pt-xs' : ''">
              <TimelineRail
                :color="markerColorFor(item)"
                :marker-class="markerClassFor(item)"
                :reserve-line-above-space="false"
                :show-line-below="rail.showLineBelow"
              />

              <div
                :class="[
                  'min-w-0 flex-1 -mt-xxs',
                  contentSpacingClassFor(
                    item,
                    nextItem,
                    groupIndex === timelineGroups.length - 1 && index === groupItems.length - 1,
                  ),
                ]"
              >
                <!-- edit opens the same composer, prefilled, in place of the entry (saves via updateProtocolEntry) -->
                <SupportMeasureQuickEntryComposer
                  v-if="item.kind === 'protocol' && editingId === item.protocol.id"
                  :support-measure-entry-id="entryId"
                  :school-subject-id="schoolSubjectId"
                  :school-class-id="schoolClassId"
                  :students="students"
                  :selected-student-id="null"
                  :connected-competence-ids="item.protocol.competences.map((competence) => competence.competenceId)"
                  :edit-protocol-entry="item.protocol"
                  @saved="editingId = null"
                  @cancel="editingId = null"
                />
                <SupportMeasureProtocolEntryItem
                  v-else-if="item.kind === 'protocol'"
                  :protocol="item.protocol"
                  :school-subject-id="schoolSubjectId"
                  :selected-student-id="selectedStudentId"
                  :readonly="readonly"
                  @request-edit="handleRequestEdit(item.protocol.id)"
                  @request-delete="removeEntry(item.protocol.id)"
                  @rate="handleRate(item.protocol.id, $event)"
                  @update:observation-level="handleObservationLevelChange(item.protocol.id, $event)"
                />

                <SupportMeasureCompletionItem
                  v-else-if="item.kind === 'completion'"
                  :completed-at="item.date"
                  :note="completionNote"
                  :selected-student-id="selectedStudentId"
                  :students="students"
                />

                <SupportMeasureActivityCompactItem
                  v-else
                  :activities="item.activities"
                  :selected-student-id="selectedStudentId"
                />
              </div>
            </div>
          </template>
        </TimelineItemList>
      </template>
    </div>
  </div>
</template>
